import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import sortBy from 'lodash/sortBy';

import { DetailMonitoringValidationComponent } from '../../../shared/components/detail-monitoring-validation/detail-monitoring-validation.component';
import { MonitoringAreasTableComponent } from '../../../shared/components/monitoring-areas-table/monitoring-areas-table.component';
import {
  MonitoringAreaTableItem,
  MonitoringAreaTableConfig,
} from '../../../shared/components/monitoring-areas-table/monitoring-areas-table.types';
import { EnhancedLoadingService } from '../../../shared/services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { DetailStatusMonitoringComponent } from './detail-status-monitoring/detail-status-monitoring.component';
import type { contractOutput } from '#backend/contract';
import { MatTabsModule } from '@angular/material/tabs';
import { SnackbarService } from '../../../shared/services/snackbar.service';

type BatchMonitoringRequest = contractOutput['monitoring']['byId'];

@Component({
  selector: 'app-monitoring-safras-detail',
  standalone: true,
  imports: [
    DetailStatusMonitoringComponent,
    MatIconModule,
    MatButtonModule,
    DetailMonitoringValidationComponent,
    MonitoringAreasTableComponent,
    MatTabsModule,
  ],
  templateUrl: './monitoring-safras-detail.component.html',
  styleUrl: './monitoring-safras-detail.component.scss',
})
export class MonitoringSafrasDetailComponent implements OnInit {
  private orpc = inject(SmartOrpcClientService);
  private loadingService = inject(EnhancedLoadingService);
  private snackBar = inject(SnackbarService);
  id: string | null = null;
  data: BatchMonitoringRequest | null = null;

  // Monitoring Areas Table Properties
  areasDataSource = new MatTableDataSource<MonitoringAreaTableItem>([]);
  areasLength = 0;
  areasIsLoading = false;
  get areasTableConfig(): MonitoringAreaTableConfig {
    return {
      showActionsColumn: false,
      columns: [
        'id',
        'carFieldValue',
        'contractFieldValue',
        'uniqueTerritoryFieldValue',
        'insurancePolicyFieldValue',
        'producerIdFieldValue',
        'identityDocumentFieldValue',
        'customerFarmNameValue',
        'areaOperationHa',
        'city',
        'state',
        'createdDate',
      ],
      initialSort: { active: 'createdDate', direction: 'desc' },
      fieldKeys: {
        carFieldKey: this.data?.carFieldKey || undefined,
        contractFieldKey: this.data?.contractFieldKey || undefined,
        uniqueTerritoryFieldKey:
          this.data?.uniqueTerritoryFieldKey || undefined,
        insurancePolicyKey: this.data?.insurancePolicyKey || undefined,
        producerIdFieldKey: this.data?.producerIdFieldKey || undefined,
        identityDocumentFieldKey:
          this.data?.identityDocumentFieldKey || undefined,
        customerFarmNameKey: this.data?.customerFarmNameKey || undefined,
      },
    };
  }
  areasCurrentPage = 0;
  areasPageSize = 10;
  areasCurrentSearch = '';

  get canApproveOrReject() {
    return (
      this.getLastFile()?.status.id == 'MANUAL_VALIDATION_IN_PROGRESS' &&
      this.data?.status?.code === 'VALIDATING_INPUT'
    );
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) {}
  ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id');
    this.route.queryParamMap.subscribe(async () => {
      this.loadData();
    });
  }

  async loadData() {
    if (this.id) {
      this.data = await this.orpc.client.monitoring.byId(Number(this.id));
      if (this.data?.monitoringAreas) {
        this.data.monitoringAreas = sortBy(
          this.data?.monitoringAreas,
          (v) => v.createdDate,
        );
      }
      // Carregar dados das áreas de monitoramento
      await this.loadMonitoringAreas();
    }
  }

  async loadMonitoringAreas() {
    if (!this.id) return;

    this.areasIsLoading = true;
    try {
      const response = await this.orpc.client.monitoring.areas.list({
        monitoringId: Number(this.id),
        textSearch: this.areasCurrentSearch,
        page: this.areasCurrentPage + 1, // Backend usa 1-based
        limit: this.areasPageSize,
      });

      this.areasDataSource.data = response.items.map((area) => ({
        id: area.id ?? 0,
        carFieldValue: area.carFieldValue || '-',
        contractFieldValue: area.contractFieldValue || '-',
        uniqueTerritoryFieldValue: area.uniqueTerritoryFieldValue || '-',
        insurancePolicyFieldValue: area.insurancePolicyFieldValue || '-',
        producerIdFieldValue: area.producerIdFieldValue || '-',
        identityDocumentFieldValue: area.identityDocumentFieldValue || '-',
        customerFarmNameValue: area.customerFarmNameValue || '-',
        areaOperationHa: area.areaOperationHa
          ? `${Number(area.areaOperationHa)?.toFixed(2)} ha`
          : '-',
        city: area.ibgeCity?.nmMun || '-',
        state: area.ibgeCity?.siglaUf || '-',
        createdBy: area.createdBy || '-',
        createdDate: area.createdDate ? area.createdDate.toString() : '-',
      }));

      this.areasLength = response.total;
    } catch (error) {
      console.error('Erro ao carregar áreas de monitoramento:', error);
    } finally {
      this.areasIsLoading = false;
    }
  }

  onAreasSearch(searchTerm: string) {
    this.areasCurrentSearch = searchTerm;
    this.areasCurrentPage = 0; // Reset para primeira página
    this.loadMonitoringAreas();
  }

  onAreasPageChange(event: PageEvent) {
    this.areasCurrentPage = event.pageIndex;
    this.areasPageSize = event.pageSize;
    this.loadMonitoringAreas();
  }

  onAreasSort(sort: Sort) {
    // Por enquanto, deixamos a ordenação simples no frontend
    if (!sort.active || sort.direction === '') {
      return;
    }

    const data = this.areasDataSource.data.slice();
    this.areasDataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'id':
          return this.compare(a.id, b.id, isAsc);
        case 'carFieldValue':
          return this.compare(a.carFieldValue, b.carFieldValue, isAsc);
        case 'contractFieldValue':
          return this.compare(
            a.contractFieldValue,
            b.contractFieldValue,
            isAsc,
          );
        case 'uniqueTerritoryFieldValue':
          return this.compare(
            a.uniqueTerritoryFieldValue,
            b.uniqueTerritoryFieldValue,
            isAsc,
          );
        case 'insurancePolicyFieldValue':
          return this.compare(
            a.insurancePolicyFieldValue,
            b.insurancePolicyFieldValue,
            isAsc,
          );
        case 'producerIdFieldValue':
          return this.compare(
            a.producerIdFieldValue,
            b.producerIdFieldValue,
            isAsc,
          );
        case 'identityDocumentFieldValue':
          return this.compare(
            a.identityDocumentFieldValue,
            b.identityDocumentFieldValue,
            isAsc,
          );
        case 'customerFarmNameValue':
          return this.compare(
            a.customerFarmNameValue,
            b.customerFarmNameValue,
            isAsc,
          );
        case 'areaOperationHa':
          return this.compare(a.areaOperationHa, b.areaOperationHa, isAsc);
        case 'city':
          return this.compare(a.city, b.city, isAsc);
        case 'state':
          return this.compare(a.state, b.state, isAsc);
        case 'createdBy':
          return this.compare(a.createdBy, b.createdBy, isAsc);
        case 'createdDate':
          return this.compare(a.createdDate, b.createdDate, isAsc);
        default:
          return 0;
      }
    });
  }

  private compare(
    a: string | number,
    b: string | number,
    isAsc: boolean,
  ): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  private getLastFile() {
    return this.data?.fileValidations?.slice(-1)[0];
  }

  alert(message: string) {
    this.snackBar.alert(message);
  }

  prompt(message: string, defaultValue: string) {
    return window.prompt(message, defaultValue);
  }

  approve() {
    const lastFileValidation = this.getLastFile();
    if (lastFileValidation?.id == null) {
      throw new Error('Houve um erro ao aprovar a validação do arquivo.');
    }

    const confirmApprove = confirm(
      `Tem certeza que deseja aprovar a validação do arquivo ${lastFileValidation.id}?`,
    );
    if (confirmApprove) {
      this.loadingService.show();
      this.orpc.client.monitoring.fileValidation
        .approve({
          fileValidationId: lastFileValidation.id,
        })
        .then(() => {
          this.alert(
            `Validação do arquivo ${lastFileValidation.id} aprovada com sucesso!`,
          );
          // Recarrega os dados
          this.loadData();
        })
        .finally(() => {
          this.loadingService.hide();
        });
    }
  }

  reject() {
    const lastFileValidation = this.getLastFile();
    if (lastFileValidation?.id == null) {
      throw new Error('Houve um erro ao rejeitar a validação do arquivo.');
    }

    // const reason = this.prompt(
    //   'Digite o motivo da rejeição da validação:',
    //   'Motivo não informado',
    // );

    this.loadingService.show();
    this.orpc.client.monitoring.fileValidation
      .reject({
        fileValidationId: lastFileValidation.id,
      })
      .then(() => {
        this.alert(
          `Validação do arquivo ${lastFileValidation.id} rejeitada com sucesso!`,
        );
        // Recarrega os dados
        this.loadData();
      })
      .finally(() => {
        this.loadingService.hide();
      });
  }
  navigateToReportUpload() {
    this.router.navigate([
      '/monitoring/adm/detail/' + this.id + '/upload-report',
    ]);
  }
}
